import { useState, useEffect, useRef, useMemo } from 'react';
import { useUpdateEffect, useMemoizedFn } from 'ahooks';
import { useForm } from 'react-final-form-hooks';
import useTeaPagination from '../useTeaPagination';

const useTeaTable = (service, options = {}) => {
  const {
    defaultParams = {}, // 当每次请求需要带一些额外的参数时，可以传入defaultParams
    manual = false, // 是否是人工模式，默认false，会自动发一次请求
    refreshDeps = [],
    ready = true,
    tableListFieldName = 'list',
    sortNoBackFirstPage = false, // 当排序改变发请求时，是否不回到第一页，默认为false，回到第一页
    clientPaging = false, // 是否前端分页，默认false，请求后端
    finalFormConfig,
    initialSorter, // 表格默认的排序
    onSortsRequest, // 修改发请求时排序相关的参数
    ...rest
  } = options;
  let formParams = null;

  if (finalFormConfig) {
    const { initialValues = {}, onSubmit, validate = null } = finalFormConfig;
    formParams = useForm({
      initialValuesEqual: () => true,
      initialValues,
      onSubmit:
        onSubmit ||
        ((values) => {
          return Promise.resolve(values);
        }),
      validate,
    });
  }
  const searchOptionRef = useRef({});
  const sortsRef = useRef(initialSorter);
  const [sorts, setSorts] = useState(initialSorter);
  const [initialLoading, setInitialLoading] = useState(true);

  const result = useTeaPagination(service, {
    manual: true,
    formParams,
    sortsRef,
    clientPaging,
    onSortsRequest,
    ...rest,
  });

  const { params = [], data, loading } = result;

  const _submit = (pagingOption = {}, searchOption = {}) => {
    if (!ready) {
      return;
    }
    let pagination = {};
    // 如果重新发请求，没有pageIndex，那么就要回到第一页
    if (!pagingOption.pageIndex) {
      pagingOption.pageIndex = 1;
    }
    if (clientPaging) {
      const { onPagingChange } = result.pagination;
      onPagingChange(pagingOption);
    } else {
      pagination = {
        pageSize: options.defaultPageSize || 10,
        ...(params?.[0] || {}),
        pageIndex: 1,
        ...pagingOption,
      };
    }

    let sortParams = { sorter: sortsRef.current };
    if (typeof onSortsRequest === 'function') {
      sortParams = onSortsRequest(sortsRef.current);
    }

    if (!formParams) {
      result.run({
        ...defaultParams,
        ...pagination,
        ...sortParams,
      });
    } else {
      setTimeout(() => {
        const { form } = formParams;
        form
          .submit()
          ?.then(async (values = {}) => {
            await result.runAsync({
              ...defaultParams,
              ...pagination,
              ...sortParams,
              ...values,
              ...searchOption,
            });
            setInitialLoading(false);
          })
          .catch((err) => err);
      });
    }
  };

  const reset = () => {
    if (formParams) {
      const { form } = formParams;
      form.restart();
    }
    _submit();
  };

  const submit = (searchOption = {}) => {
    // e?.preventDefault?.();
    const { sort_type: sortType, sort, ...restSearchOption } = searchOption || {};
    searchOptionRef.current = { ...restSearchOption };
    _submit({}, searchOption);
  };

  const onSortsChange = (value) => {
    let newValue = value;
    // 可传入自定义的排序修改后的回调函数，返回请求时传递的参数
    if (typeof options.onSortsChange === 'function') {
      newValue = options.onSortsChange(value);
    }
    sortsRef.current = newValue;
    setSorts(newValue);
    let pagingOption = null;
    if (sortNoBackFirstPage) {
      const pageIndex = params?.[0]?.pageIndex;
      pagingOption = { pageIndex: pageIndex || 1 };
    } else {
      pagingOption = { pageIndex: 1 };
    }
    _submit(pagingOption, searchOptionRef.current);
  };

  // init
  useEffect(() => {
    // if has cache, use cached params. ignore manual and ready.
    if (params?.length > 0) {
      result.run(...params);
      return;
    }
    if (!manual && ready) {
      _submit();
    }
  }, []);

  // refresh & ready change on the same time
  const hasAutoRun = useRef(false);
  hasAutoRun.current = false;

  useUpdateEffect(() => {
    if (!manual && ready) {
      hasAutoRun.current = true;
      // if (form) {
      //   form.resetFields();
      // }
      // allFormDataRef.current = {};
      // restoreForm();
      _submit();
    }
  }, [ready]);

  useUpdateEffect(() => {
    if (hasAutoRun.current) {
      return;
    }
    if (!ready) {
      return;
    }
    if (!manual) {
      hasAutoRun.current = true;
      result.pagination.changePageIndex(1);
    }
  }, [...refreshDeps]);

  return {
    ...result,
    tableProps: {
      dataSource: data ? data[tableListFieldName] || [] : [],
      loading: initialLoading || loading,
      sorter: {
        sorts,
        onSortsChange: onSortsChange,
      },
      pagination: {
        pageIndex: result.pagination.pageIndex,
        pageSize: result.pagination.pageSize,
        recordCount: result.pagination.recordCount,
        onPagingChange: result.pagination.onPagingChange,
      },
    },
    search: {
      ...(formParams || {}),
      submit: useMemoizedFn(submit),
      reset: useMemoizedFn(reset),
    },
  };
};

export default useTeaTable;
